import { Box } from '@mui/material';
import FormFields from '../../../../form/FormFields';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import VideocamIcon from '@mui/icons-material/Videocam';
import StartIcon from '@mui/icons-material/Start';
import KeyboardTabIcon from '@mui/icons-material/KeyboardTab';
import EditNoteIcon from '@mui/icons-material/EditNote';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import DateRangeIcon from '@mui/icons-material/DateRange';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ExoDraftManager from '../../../ExoDraftManager';
import SafetyCheckIcon from '@mui/icons-material/SafetyCheck';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useDebounce } from 'use-debounce';

export const CalMeetingForm = ({
  dataCollection,
  onChange,
  calendarOptions,
  onValidationChange,
}) => {
  const { t } = useTranslation();
  const [lastChange, setLastChange] = useState({});
  const [debouncedDataCollection] = useDebounce(lastChange, 700);

  const fieldsWholeDay = [
    {
      label: t('Name'),
      key: 'label',
      type: 'text',
      colspan: 2,
    },

    {
      label: t('Start Date'),
      key: 'startDate',
      type: 'date',
      colspan: 1,
      Icon: StartIcon,
    },
    {
      label: t('End Date'),
      key: 'endDate',
      type: 'date',
      colspan: 1,
      Icon: KeyboardTabIcon,
    },
    {
      label: t('Whole Day'),
      key: 'wholeDay',
      type: 'checkbox',
      colspan: 2,
      Icon: DateRangeIcon,
    },

    {
      label: t('Online Meeting'),
      key: 'onlineMeeting',
      type: 'meeting',
      Icon: VideocamIcon,
      colspan: 2,
    },
    {
      label: t('Location'),
      key: 'location',
      type: 'address',
      Icon: LocationOnIcon,
      colspan: 2,
      allowOwnAddress: true,
      suggestionsArr: [
        {
          key: 'participants',
          isArray: true,
          type: 'participant',
        },
        {
          key: 'meetingPartner',
          isArray: false,
          type: 'client',
        },
      ],
    },
    {
      label: t('Participants'),
      key: 'participants',
      type: 'participantsAutoComplete',
      colspan: 2,
      searchParams: [
        'companies',
        'contacts',
        'person_leads',
        'company_leads',
        'users',
      ],
      suggestionsArr: [
        {
          key: 'meetingPartner',
          isArray: false,
          type: 'client',
        },
      ],
    },
    {
      label: t('Project'),
      key: 'project',
      type: 'project',
      colspan: 2,
      apiUrl: 'projects',
    },
    {
      label: t('Category'),
      key: 'category',
      type: 'eventCategory',
      colspan: 2,
    },
    {
      label: t('Agenda'),
      key: 'agenda',
      type: 'richText',
      colspan: 2,
      Icon: FormatListNumberedIcon,
    },
    {
      label: t('more options'),
      type: 'expandFields',
      colspan: 2,
      fields: [
        {
          label: t('Partner'),
          key: 'meetingPartner',
          type: 'autoCompleteClient',
          colspan: 2,
          searchParams: [
            'companies',
            'contacts',
            'person_leads',
            'company_leads',
            'users',
          ],
        },

        {
          label: t('Owner'),
          key: 'owner',
          type: 'user',
          colspan: 2,
        },
        {
          label: t('Reminder to Receiver'),
          key: 'reminders',
          type: 'reminder',
          colspan: 2,
          dateKey: 'startDate',
          timeKey: 'startTime',
          size: 'small',
        },
        {
          label: t('Meeting is already accepted'),
          key: 'meetingIsConfirmed',
          type: 'checkbox',
          colspan: 2,
          Icon: DoneAllIcon,
        },
        {
          label: t('Block time period for all participating employees'),
          key: 'blockTime',
          type: 'checkbox',
          colspan: 2,
          Icon: SafetyCheckIcon,
        },
        {
          label: t('Internal note'),
          key: 'internalNote',
          type: 'richText',
          colspan: 2,
          Icon: EditNoteIcon,
        },
      ],
    },
  ];
  const validationSchemaWholeDay = Yup.object().shape({
    startDate: Yup.string().required(t('Is required')),
    endDate: Yup.string().required(t('Is required')),
    label: Yup.string().required(t('Is required')),
    /*onlineMeeting: Yup.object()
      .shape({
        type: Yup.string().nullable(),
        url: Yup.string().url().nullable(),
      })
      .nullable(),*/
    owner: Yup.object().shape({ id: Yup.string().required() }),
    location: Yup.object()
      .shape({
        street: Yup.string(),
        houseNumber: Yup.string(),
        zipCode: Yup.string(),
        city: Yup.string(),
        countryCode: Yup.string(),
      })
      .nullable(),
  });

  const fieldsHalfDay = [
    {
      label: t('Name'),
      key: 'label',
      type: 'text',
      colspan: 2,
    },
    {
      label: t('Start Date'),
      key: 'startDate',
      type: 'date',
      colspan: 1,
      Icon: StartIcon,
    },
    {
      label: t('Start Time'),
      key: 'startTime',
      type: 'time',
      colspan: 1,
    },
    {
      label: t('End Date'),
      key: 'endDate',
      type: 'date',
      colspan: 1,
      Icon: KeyboardTabIcon,
    },
    {
      label: t('End Time'),
      key: 'endTime',
      type: 'time',
      colspan: 1,
    },
    {
      label: t('Whole Day'),
      key: 'wholeDay',
      type: 'checkbox',
      colspan: 2,
      Icon: DateRangeIcon,
    },

    {
      label: t('Online Meeting'),
      key: 'onlineMeeting',
      type: 'meeting',
      Icon: VideocamIcon,
      colspan: 2,
    },
    {
      label: t('Location'),
      key: 'location',
      type: 'address',
      Icon: LocationOnIcon,
      colspan: 2,
      allowOwnAddress: true,
      suggestionsArr: [
        {
          key: 'participants',
          isArray: true,
          type: 'participant',
        },
        {
          key: 'meetingPartner',
          isArray: false,
          type: 'client',
        },
      ],
    },
    {
      label: t('Participants'),
      key: 'participants',
      type: 'participantsAutoComplete',
      colspan: 2,
      searchParams: [
        'companies',
        'contacts',
        'person_leads',
        'company_leads',
        'users',
      ],
      suggestionsArr: [
        {
          key: 'meetingPartner',
          isArray: false,
          type: 'client',
        },
      ],
    },
    {
      label: t('Project'),
      key: 'project',
      type: 'project',
      colspan: 2,
      apiUrl: 'projects',
    },
    {
      label: t('Category'),
      key: 'category',
      type: 'eventCategory',
      colspan: 2,
    },

    {
      label: t('Agenda'),
      key: 'agenda',
      type: 'richText',
      colspan: 2,
      Icon: FormatListNumberedIcon,
    },

    {
      label: t('more options'),
      type: 'expandFields',
      colspan: 2,
      fields: [
        {
          label: t('Partner'),
          key: 'meetingPartner',
          type: 'autoCompleteClient',
          colspan: 2,
          searchParams: [
            'companies',
            'contacts',
            'person_leads',
            'company_leads',
            'users',
          ],
        },

        {
          label: t('Owner'),
          key: 'owner',
          type: 'user',
          colspan: 2,
        },
        {
          label: t('Reminder to Receiver'),
          key: 'reminders',
          type: 'reminder',
          colspan: 2,
          dateKey: 'startDate',
          timeKey: 'startTime',
          size: 'small',
        },
        {
          label: t('Meeting is already accepted'),
          key: 'meetingIsConfirmed',
          type: 'checkbox',
          colspan: 2,
          Icon: DoneAllIcon,
        },
        {
          label: t('Block time period for all participating employees'),
          key: 'blockTime',
          type: 'checkbox',
          colspan: 2,
          Icon: SafetyCheckIcon,
        },
        {
          label: t('Internal note'),
          key: 'internalNote',
          type: 'richText',
          colspan: 2,
          Icon: EditNoteIcon,
        },
      ],
    },
  ];
  const validationSchemaHalfDay = Yup.object().shape({
    startDate: Yup.string().required(t('Is required')),
    startTime: Yup.string().required(t('Is required')),
    endDate: Yup.string().required(t('Is required')),
    endTime: Yup.string().required(t('Is required')),
    label: Yup.string().required(t('Is required')),
    /*onlineMeeting: Yup.object()
      .shape({
        type: Yup.string().nullable(),
        url: Yup.string().url().nullable(),
      })
      .nullable(),*/
    location: Yup.object()
      .shape({
        street: Yup.string(),
        houseNumber: Yup.string(),
        zipCode: Yup.string(),
        city: Yup.string(),
        countryCode: Yup.string(),
      })
      .nullable(),
  });

  useEffect(() => {
    validateValues(debouncedDataCollection);
  }, [debouncedDataCollection]);

  function validateValues(last) {
    const { data, change } = last;
    if (!data || !data.startDate || !data.endDate) return;

    var newDataCollection = { ...data };

    // check if startDate is before endDate
    const startDate = moment(data.startDate || dataCollection.startDate);
    const endDate = moment(data.endDate || dataCollection.endDate);

    if (startDate.isAfter(endDate)) {
      if (change.startDate) newDataCollection.endDate = data.startDate;
      if (change.endDate) newDataCollection.startDate = data.endDate;
    }

    // time if its the same day
    const startTime = moment(data.startTime || dataCollection.startTime);
    const endTime = moment(data.endTime || dataCollection.endTime);

    if (startDate.isSame(endDate, 'day') && startTime.isAfter(endTime)) {
      if (change.startTime)
        newDataCollection.endTime = startTime.add(30, 'm').toISOString();
      if (change.endTime)
        newDataCollection.startTime = endTime.subtract(30, 'm').toISOString();
    }

    onChange(newDataCollection);
  }

  return (
    <Box className="grid grid-cols-2 gap-3 w-full">
      <ExoDraftManager
        draftType="meeting"
        titleFunction={(e) => e.label}
        dataCollection={dataCollection}
        setDataCollection={onChange}
        onChange={onChange}
      />
      <FormFields
        fields={dataCollection.wholeDay == '1' ? fieldsWholeDay : fieldsHalfDay}
        dataCollection={dataCollection}
        updateDataCollection={(data) => {
          setLastChange({
            change: data.reminders ? lastChange.change : data,
            data: { ...dataCollection, ...data },
          });
          onChange(data);
        }}
        validationSchema={
          dataCollection.wholeDay == '1'
            ? validationSchemaWholeDay
            : validationSchemaHalfDay
        }
        size={'small'}
        onValidationChange={onValidationChange}
      />
    </Box>
  );
};
